import React from 'react';
import { MdClose } from 'react-icons/md';
import { useId } from '@reach/auto-id';
import { DialogContent, DialogOverlay, DialogProps } from '@reach/dialog';
import classNames from 'classnames';

import { Button } from '../button';
import { Heading } from '../heading';
import { Icon } from '../icon';
import { IconContainer } from '../icon-container';

export type ModalProps = {
  heading?: string;
  headingIcon?: React.ReactNode;
  variant?: 'default' | 'success' | 'danger';
  size?: 'sm' | 'md' | 'lg' | 'xl';
  isCloseButtonVisible?: boolean;
  children?: React.ReactNode;
} & Pick<DialogProps, 'isOpen' | 'initialFocusRef' | 'onDismiss'>;

export const Modal = ({
  heading,
  headingIcon,
  variant,
  size = 'md',
  children,
  isOpen,
  isCloseButtonVisible = true,
  initialFocusRef,
  onDismiss,
}: ModalProps) => {
  const labelId = useId('modal-label');
  return (
    <DialogOverlay
      className="fixed inset-0 flex bg-black bg-opacity-40 overflow-auto z-20"
      isOpen={isOpen}
      initialFocusRef={initialFocusRef}
      onDismiss={onDismiss}
    >
      <DialogContent
        className={classNames(
          'flex-1 m-auto p-8 rounded-lg bg-white shadow focus:outline-none',
          {
            'max-w-md': size === 'sm',
            'max-w-2xl': size === 'md',
            'max-w-3xl': size === 'lg',
            'max-w-6xl': size === 'xl',
          }
        )}
        aria-labelledby={labelId}
      >
        {heading && (
          <div className="flex items-center space-x-3 mb-8">
            {headingIcon && (
              <IconContainer
                variant={
                  variant === 'danger' || variant === 'success'
                    ? variant
                    : 'light'
                }
              >
                {headingIcon}
              </IconContainer>
            )}
            <Heading
              variant={variant}
              size="xl"
              className="flex-1"
              id={labelId}
            >
              {heading}
            </Heading>
            {isCloseButtonVisible && onDismiss && (
              <Button
                variant="ghost"
                className="self-start text-gray-300"
                aria-label="Close"
                onClick={onDismiss}
                size="sm"
              >
                <Icon icon={<MdClose />} />
              </Button>
            )}
          </div>
        )}
        {children}
      </DialogContent>
    </DialogOverlay>
  );
};
